import React, { useEffect, useMemo, useRef, useState, useCallback, useDeferredValue } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  Row,
  Table,
} from "reactstrap";
import api from "../../api";
import { useAppDialog } from "./AppDialogProvider";
import { getMafiaEmployeeName, getMafiaEmpId, resolveEmployeeNames } from "./mafiaIdentity";
import { MafiaPageHeader } from "./_shared";
import MafiaStatusTimeline from "./MafiaStatusTimeline";
import MafiaTheme from "./MafiaTheme";
import FrmSelect from "./FrmSelect";

const ACCEPTABLE_DIM_TYPES = [
  "Angle",
  "Basic",
  "Bulk Shaft",
  "Chamfer",
  "Check w/ Gauge*",
  "Copper Sulfate*",
  "Diameter",
  "Fillet",
  "Final Inspection*",
  "GD&T",
  "Heat Treat*",
  "Humidity Chamber*",
  "Lead Note",
  "Length",
  "Major Diameter",
  "Material Verification",
  "Minor Diameter",
  "Pass/Fail*",
  "Passivation*",
  "Pitch Diameter",
  "Plating*",
  "Radius",
  "Salt Fog*",
  "Spline",
  "Surface Finish",
  "Test",
  "Text Note",
  "Thread",
  "Title Note",
  "Torque (Cal)",
  "Weld*",
  "Weld Finish",
];

const METHOD_OPTIONS = [
  "X - Pass/Fail, Record in SSW Form",
  "Y - Pass/Fail, Record in Customer Approved Form",
  "A - Measure Variable, Record in SSW Form",
  "B - Measure Variable, Record in Customer Approved Form",
  " ",
];

const TOLERANCE_OPTIONS = [
  "Superfine",
  "Fine",
  "Medium",
  "Coarse",
  "Very Coarse",
  "100%",
  "First piece only",
  "Reference",
  "Programming Reference",
  "Gage Reference",
  "Basic Dimension",
  " ",
];

const COLUMN_DEFS = [
  // Round 6 (Batch 3): MOM OP # moved to the FRONT (was last). This single array
  // drives both the header row and every data row, so reordering here moves the
  // column everywhere in the Edit Part grid at once. Label also updated to the
  // canonical "MOM OP #".
  { key: "MOMOpNumber", label: "MOM OP #", type: "input", width: 100 },
  { key: "DNA", label: "DNA", type: "input", width: 110 },
  { key: "DimensionType", label: "Dimension Type", type: "select", options: ACCEPTABLE_DIM_TYPES, width: 200 },
  { key: "Unit", label: "Unit", type: "input", width: 120 },
  { key: "Nominal", label: "Nominal", type: "input", width: 120 },
  { key: "Min", label: "Min", type: "input", width: 120 },
  { key: "Max", label: "Max", type: "input", width: 120 },
  { key: "ToleranceClass", label: "Tolerance Class", type: "select", options: TOLERANCE_OPTIONS, width: 190 },
  { key: "InProcess", label: "In Process", type: "input", width: 150 },
  { key: "MethodOfRecording", label: "Method Of Recording", type: "select", options: METHOD_OPTIONS, width: 240 },
  { key: "SpecialInstructions", label: "Special Instructions", type: "longtext", width: 220 },
  { key: "WIATPNumber", label: "WI or ATP Number", type: "input", width: 180 },
  { key: "Gages", label: "Gages", type: "datalist", width: 170 },
  { key: "AdditionalInfo", label: "Additional Info", type: "longtext", width: 240 },
];

function blankRow() {
  return {
    DNA: "",
    MOMOpNumber: "",
    DimensionType: "",
    Unit: "",
    Nominal: "",
    Min: "",
    Max: "",
    ToleranceClass: "",
    InProcess: "",
    MethodOfRecording: "",
    SpecialInstructions: "",
    WIATPNumber: "",
    Gages: "",
    AdditionalInfo: "",
  };
}

export default function EditPart({
  partNum = "",
  rev = 0,
  revMinor = 0,
  isImported = false,
  isNew = false,
  onBack,
  onOpenFrmSelect,
  onDone,
  // Go-live parity (2026-09-24): re-opens this editor on another revision
  // (used after Save Unfinished stores a finished revision's draft as a new
  // MAFIA revision, so the user keeps editing that draft).
  onOpenRevision,
}) {
  const dialog = useAppDialog();
  const normalizedPartNum = String(partNum || "").trim().toUpperCase();

  const [rows, setRows] = useState([]);
  const [gageOptions, setGageOptions] = useState([" "]);
  const [childParts, setChildParts] = useState([]);
  const [childInput, setChildInput] = useState("");

  const [loading, setLoading] = useState(true);
  // Round 64 (UAT: "no id please"): human name of the CURRENT user for the
  // "Locked by" header — previously, when you held the lock yourself the
  // header fell back to the raw EmpId (e.g. "Locked by 3325").
  const [currentUserDisplay, setCurrentUserDisplay] = useState("");
  // Round 63: when the load chain fails, remember WHICH call and WHY so the
  // page can show it and offer Retry instead of a dead generic dialog.
  const [loadError, setLoadError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const [history, setHistory] = useState("");
  const [createdByLabel, setCreatedByLabel] = useState("");
  // Round 8 (Batch 2): resolved "First Last (id)" versions of the raw identity
  // strings shown in the header (Last revised by / Locked by). They default to
  // the raw value and are upgraded to names after the async resolver returns.
  const [createdByDisplay, setCreatedByDisplay] = useState("");
  const [lockOwnerDisplay, setLockOwnerDisplay] = useState("");
  const [currentUsername, setCurrentUsername] = useState("");

  const [revMaj, setRevMaj] = useState(rev);
  const [revMin, setRevMin] = useState(revMinor);

  const [manualRevMaj, setManualRevMaj] = useState(String(rev));
  const [manualRevMin, setManualRevMin] = useState(String(revMinor));

  const [qtyForSampling, setQtyForSampling] = useState("");
  const [partText, setPartText] = useState(normalizedPartNum);
  const [keepOpenAfterSave, setKeepOpenAfterSave] = useState(false);
  // Go-live parity (2026-09-24, audit N5): verifier selection shown over the
  // editor when "Keep page open after save" is ticked.
  const [keepOpenSelect, setKeepOpenSelect] = useState(null);
  const [activeCell, setActiveCell] = useState(null);
  const [lockOwner, setLockOwner] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [errorText, setErrorText] = useState("");
  // UI redesign (per approved mockup): compact density toggle. Advisory only —
  // does not affect any save/lock/validation behavior.
  const [compact, setCompact] = useState(false);
  // Round 66 (UAT: "user should be able to pin ANY columns whatever they
  // want"): per-column pinning. Every header has a pin icon; pinned columns
  // freeze from the left in grid order with offsets computed from the column
  // widths (plus the 50px # gutter, which freezes automatically whenever at
  // least one column is pinned). The set persists per browser.
  const [pinnedKeys, setPinnedKeys] = useState(() => {
    try {
      const raw = window.localStorage.getItem("mafiaEditPart.pinnedCols");
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) return arr.filter((k) => COLUMN_DEFS.some((c) => c.key === k));
      }
      // Migration from the Round 65 on/off toggle.
      if (window.localStorage.getItem("mafiaEditPart.pinCols") === "off") return [];
    } catch (_) { /* default below */ }
    return ["MOMOpNumber", "DNA"];
  });
  const togglePin = useCallback((key) => {
    setPinnedKeys((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      try { window.localStorage.setItem("mafiaEditPart.pinnedCols", JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }, []);
  const clearPins = useCallback(() => {
    setPinnedKeys([]);
    try { window.localStorage.setItem("mafiaEditPart.pinnedCols", "[]"); } catch (_) {}
  }, []);

  // Left offset per pinned column, in COLUMN_DEFS display order, starting
  // after the 50px # gutter. Pinned columns get a FIXED width (their defined
  // width) so the offsets line up exactly. The last pinned column carries the
  // divider shadow.
  const pinInfo = useMemo(() => {
    const orderedPinned = COLUMN_DEFS.filter((c) => pinnedKeys.includes(c.key));
    const map = new Map();
    let left = 50;
    orderedPinned.forEach((c, i) => {
      map.set(c.key, { left, width: c.width, last: i === orderedPinned.length - 1 });
      left += c.width;
    });
    return map;
  }, [pinnedKeys]);
  const pinStyleFor = (key) => {
    const info = pinInfo.get(key);
    if (!info) return null;
    return {
      left: info.left,
      width: info.width,
      minWidth: info.width,
      maxWidth: info.width,
    };
  };

  const inputRefs = useRef({});
  const rowRefs = useRef({});
  const mountedRef = useRef(true);

  const selectedRow = activeCell?.rowIndex != null ? rows[activeCell.rowIndex] : null;
  const selectedColumn = activeCell?.field || "";

  const historyLabel = isImported ? "" : history.trim() !== "" ? `History: ${history}` : "";
  const revisedByLabel = isImported ? "" : `Last revised by: ${createdByDisplay || createdByLabel}`;

  // Round 4 (Batch 2) PERFORMANCE: typing in a cell updates `rows` immediately
  // (so the input never lags), but the expensive whole-grid derivations below
  // — full-text row filter + per-row/per-field validation — are computed from a
  // DEFERRED copy of rows. React recomputes them in a lower-priority pass a beat
  // after the keystroke, so a 40-row grid no longer re-validates every field on
  // every character. Values are identical; only the timing of the recompute
  // changes. Save/confirm still validate against the live `rows` (see
  // validateRows), so correctness is unaffected.
  const deferredRows = useDeferredValue(rows);

  const filteredRowEntries = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    const indexedRows = deferredRows.map((row, index) => ({ row, index }));

    if (!q) return indexedRows;

    return indexedRows.filter(({ row }) =>
      Object.values(row).some((value) => String(value || "").toLowerCase().includes(q))
    );
  }, [deferredRows, tableSearch]);

  // UI redesign: advisory per-cell validation (mirrors the legacy guidance —
  // dimension type required; numeric dimensions want Nominal/Min/Max + a gage;
  // MOM Op # numeric-or-blank). This is PURELY a visual aid: it highlights cells
  // and shows a summary, but it never blocks Save (legacy let users save with
  // warnings, and we preserve that exactly). Computed from deferredRows (Round 4).
  const rowErrors = useMemo(() => {
    const isNoteType = (t) => {
      const s = String(t || "").toLowerCase();
      return s.includes("note") || s.includes("title") || s.includes("*");
    };
    const map = {}; // rowIndex -> Set(field)
    deferredRows.forEach((r, i) => {
      const errs = new Set();
      const dim = String(r.DimensionType || "").trim();
      if (!dim) errs.add("DimensionType");
      const numeric =
        dim &&
        !isNoteType(dim) &&
        !["Pass/Fail*", "Reference", "Basic"].includes(dim);
      if (numeric) {
        // Go-live parity (2026-09-24): "-" counts as blank (legacy stores both as NULL).
        if (isBlankNumeric(r.Nominal)) errs.add("Nominal");
        if (isBlankNumeric(r.Min)) errs.add("Min");
        if (isBlankNumeric(r.Max)) errs.add("Max");
        if (!String(r.Gages || "").trim()) errs.add("Gages");
      }
      const mom = String(r.MOMOpNumber || "").trim();
      if (mom && !/^\d+$/.test(mom)) errs.add("MOMOpNumber");
      if (errs.size) map[i] = errs;
    });
    return map;
  }, [deferredRows]);

  const validationSummary = useMemo(() => {
    const labels = {
      DimensionType: "Dimension Type",
      Nominal: "Nominal",
      Min: "Min",
      Max: "Max",
      Gages: "Gage",
      MOMOpNumber: "MOM Op # (numeric)",
    };
    const fields = new Set();
    let rowsWithIssues = 0;
    Object.values(rowErrors).forEach((set) => {
      rowsWithIssues += 1;
      set.forEach((f) => fields.add(labels[f] || f));
    });
    return { rowsWithIssues, fields: [...fields] };
  }, [rowErrors]);

  const releaseLockIfOwned = useCallback(async () => {
    try {
      if (!normalizedPartNum || !currentUsername) return;

      const currentLockStatus = await getLockStatus({
        partNumber: normalizedPartNum,
        rev,
        revMinor,
      });

      if (
        String(currentLockStatus || "").trim().toLowerCase() ===
        currentUsername.trim().toLowerCase()
      ) {
        await setLockStatus({
          partNum: normalizedPartNum,
          rev,
          revMinor,
          newLock: "UNLOCK",
        });
      }
    } catch (error) {
      console.error("Cleanup failed:", error);
    }
  }, [normalizedPartNum, currentUsername, rev, revMinor]);

  const loadPage = useCallback(async () => {
    try {
      setLoading(true);

      if (!normalizedPartNum) {
        setRows([blankRow()]);
        return;
      }

      setLoadError("");

      const username = await editStage("Identity (mafia/whoami)", () =>
        getCurrentUsername()
      );
      if (!mountedRef.current) return;
      setCurrentUsername(username);
      getMafiaEmployeeName().then((nm) => {
        if (mountedRef.current && nm) setCurrentUserDisplay(nm);
      });

      const lockStatusRaw = await editStage("Lock status (mafia/lockstatus)", () =>
        getLockStatus({
          partNumber: normalizedPartNum,
          rev,
          revMinor,
        })
      );
      if (!mountedRef.current) return;

      // Defensive: backend may return "", a username string, null, or an
      // unexpected object on error. Coerce to a safe string before using
      // .trim() so the page never throws here.
      const lockStatus =
        typeof lockStatusRaw === "string" ? lockStatusRaw : "";

      setLockOwner(lockStatus);

      if (
        lockStatus.trim() !== "" &&
        lockStatus.trim().toLowerCase() !== username.trim().toLowerCase()
      ) {
        // Round 64: show the lock owner's NAME, not their raw id.
        let lockOwnerName = lockStatus;
        try {
          const m = await resolveEmployeeNames([lockStatus]);
          lockOwnerName = (m && m.get(lockStatus)) || lockStatus;
        } catch (_) { /* fall back to raw */ }
        await dialog.warning(`This part is already being checked by ${lockOwnerName}`);
        if (onBack) onBack();
        return;
      }

      await editStage("Acquire edit lock (mafia/set-lock-status)", () =>
        setLockStatus({
          partNum: normalizedPartNum,
          rev,
          revMinor,
          newLock: username.toLowerCase(),
        })
      );

      const [oldHistory, revisedBy, partRows, gages, children] = await editStage(
        "Part data load (mafia/old-history, revisedby, part, gagelist, children)",
        () =>
          Promise.all([
            getOldHistory({ partNumber: normalizedPartNum, rev, revMinor }),
            getRevisedBy({ partNumber: normalizedPartNum, rev, revMinor }),
            getPart({ partNumber: normalizedPartNum, rev, revMinor }),
            getGageList(),
            getChildren(normalizedPartNum),
          ])
      );

      if (!mountedRef.current) return;

      setHistory(oldHistory || "");
      setCreatedByLabel(revisedBy || "");

      // Round 8 (Batch 2): resolve the header identity strings to names in the
      // background. Defaults (set below) keep the raw value visible until the
      // resolver returns, so the header never flashes empty.
      setCreatedByDisplay(revisedBy || "");
      setLockOwnerDisplay(lockStatus || "");
      {
        const idsToResolve = [];
        if (revisedBy) idsToResolve.push(revisedBy);
        if (lockStatus) idsToResolve.push(lockStatus);
        if (idsToResolve.length > 0) {
          resolveEmployeeNames(idsToResolve).then((map) => {
            if (!mountedRef.current) return;
            if (revisedBy && map.get(revisedBy)) setCreatedByDisplay(map.get(revisedBy));
            if (lockStatus && map.get(lockStatus)) setLockOwnerDisplay(map.get(lockStatus));
          });
        }
      }
      // Round 65 (UAT: "why gauges suggestions like this"): the list used to
      // start with a blank " " entry (legacy dropdown artifact) and came in DB
      // order with duplicates. Now: trimmed, de-duplicated, alphabetical, no
      // blank suggestion. Free typing still allowed (datalist).
      setGageOptions(
        [...new Set((gages || []).map((g) => String(g ?? "").trim()).filter(Boolean))]
          .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      );
      setChildParts(children || []);

      const mappedRows = (partRows || []).map((r) => {
        const row = {
          DNA: stringOrEmpty(r.dna),
          MOMOpNumber: stringOrEmpty(r.momOpNumber),
          DimensionType: stringOrEmpty(r.dimensionType),
          Unit: stringOrEmpty(r.unit),
          Nominal: nullableDecimalToString(r.nominal),
          Min: nullableDecimalToString(r.min),
          Max: nullableDecimalToString(r.max),
          ToleranceClass: stringOrEmpty(r.toleranceClass),
          InProcess: stringOrEmpty(r.inProcess),
          MethodOfRecording: stringOrEmpty(r.methodOfRecording),
          SpecialInstructions: stringOrEmpty(r.specialInstructions),
          WIATPNumber: stringOrEmpty(r.wiatpNumber),
          Gages: stringOrEmpty(r.gages),
          AdditionalInfo: stringOrEmpty(r.additionalInfo),
        };

        if (row.DimensionType.toLowerCase().includes("note")) {
          row.AdditionalInfo = row.AdditionalInfo.toUpperCase();
        }

        return row;
      });

      setRows(mappedRows.length > 0 ? mappedRows : [blankRow()]);
    } catch (error) {
      console.error("Failed to load EditPart:", error);
      const message = error?.isEditStageError
        ? `Failed to load Edit Part — ${error.message}`
        : "Failed to load Edit Part page.";
      if (mountedRef.current) setLoadError(message);
      await dialog.error(message);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [normalizedPartNum, rev, revMinor, onBack, dialog]);

  useEffect(() => {
    mountedRef.current = true;
    loadPage();

    return () => {
      mountedRef.current = false;
    };
  }, [loadPage]);

  useEffect(() => {
    const beforeUnloadHandler = (e) => {
      if (isImported && !submitted) {
        e.preventDefault();
        e.returnValue =
          "You have not completed the import. Any existing work will be lost and a re-import will be required in the future.";
      }
    };

    window.addEventListener("beforeunload", beforeUnloadHandler);
    return () => window.removeEventListener("beforeunload", beforeUnloadHandler);
  }, [isImported, submitted]);

  useEffect(() => {
    return () => {
      releaseLockIfOwned();
    };
  }, [releaseLockIfOwned]);

  // Go-live parity (2026-09-24, audit S7): legacy "Edit A Part" listed only
  // the latest revision of each part. All Parts lists every revision (Round
  // 63), so warn when an older revision is opened for editing: saving it does
  // not update the part's current table (Save & Close may even collide with
  // an existing revision). Advisory only — nothing is blocked.
  const latestRevCheckedRef = useRef(false);
  useEffect(() => {
    if (loading || loadError || isNew || isImported || !normalizedPartNum) return;
    if (latestRevCheckedRef.current) return;
    latestRevCheckedRef.current = true;
    (async () => {
      try {
        const openedMaj = Number(rev) || 0;
        const openedMin = Number(revMinor) || 0;
        const latestMaj = Number(await getMaxPrintRev(normalizedPartNum)) || 0;
        if (latestMaj < openedMaj) return;
        const latestMin = Number(await getLatestRevMinor({ partNum: normalizedPartNum, rev: latestMaj })) || 0;
        if (!mountedRef.current) return;
        if (latestMaj > openedMaj || latestMin > openedMin) {
          await dialog.warning(
            `You opened revision ${openedMaj}.${openedMin} of ${normalizedPartNum}, but the latest revision is ${latestMaj}.${latestMin}. ` +
            `Changes saved from an older revision do not update the part's current MAFIA table. ` +
            `Unless you really mean to work on this old revision, go back and open revision ${latestMaj}.${latestMin}.`
          );
        }
      } catch (err) {
        console.error("Latest-revision check failed (advisory only)", err);
      }
    })();
  }, [loading, loadError, isNew, isImported, normalizedPartNum, rev, revMinor, dialog]);

  // Round 4 (Batch 2): useCallback so the onChange handed to every memoized
  // EditableCell is a stable reference across renders. Depends only on
  // gageOptions (used in the Gages branch); setRows/setGageOptions/setErrorText
  // are stable setState refs.
  const setRowValue = useCallback((index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      const row = { ...next[index] };
      row[field] = value;

      if (field === "Gages" && value && !gageOptions.includes(value)) {
        setGageOptions((opts) => [...opts, value]);
      }

      if (field === "Gages" && value.trim().toLowerCase() === "pin gauge") {
        row.MethodOfRecording = "X - Pass/Fail, Record in SSW Form";
      }

      if (field === "DimensionType") {
        if (row.ToleranceClass.toLowerCase().includes("reference")) {
          row.AdditionalInfo = encodeReferenceSymbols(row.AdditionalInfo);
        }
      }

      if (field === "ToleranceClass") {
        if (value.toLowerCase().includes("reference")) {
          row.AdditionalInfo = encodeReferenceSymbols(row.AdditionalInfo);
        } else if (
          row.DimensionType.toLowerCase().includes("note") ||
          row.DimensionType.toLowerCase().includes("finish")
        ) {
          row.AdditionalInfo = decodeReferenceSymbols(row.AdditionalInfo);
        }
      }

      next[index] = row;
      return next;
    });

    setErrorText("");
  }, [gageOptions]);

  function addBlankRow() {
    setRows((prev) => [...prev, blankRow()]);
  }

  function deleteCurrentRow() {
    if (!activeCell) return;
    setRows((prev) => prev.filter((_, idx) => idx !== activeCell.rowIndex));
    setActiveCell(null);
  }

  async function determineInProcess() {
    const qty = Number.parseInt(qtyForSampling.trim(), 10);
    if (Number.isNaN(qty)) {
      await dialog.warning("Please enter a valid sampling quantity.");
      return;
    }

    const nextRows = [...rows];

    for (let j = 0; j < nextRows.length; j++) {
      const row = { ...nextRows[j] };
      if (row.ToleranceClass && row.ToleranceClass.trim() !== "") {
        const multiplier = await determineMultiplier(
          row.DimensionType?.trim() || "",
          row.ToleranceClass?.trim() || "",
          qty
        );

        let result = 0;
        if (Number(multiplier) !== 0) {
          result = Math.ceil(qty * Number(multiplier));
        }

        if (result !== 0) {
          row.InProcess = `Every ${Math.floor(qty / result)}`;
        }

        nextRows[j] = row;
      }
    }

    setRows(nextRows);
  }

  async function copyCurrentRowToNewDNA() {
    const newDNANumber = await dialog.prompt({
      title: "Copy Row to New DNA",
      message:
        "Please enter the new DNA number to which this line will be copied to. If you change the DNA number afterwards, you may lose some information on the copied line.",
      placeholder: "Enter DNA number",
      confirmText: "Copy",
      cancelText: "Cancel",
      validateInput: (value) => {
        if (!value.trim()) return "DNA number is required.";
        return true;
      },
    });

    if (!newDNANumber) return;

    const normalized = newDNANumber.trim().toUpperCase();
    if (!normalized) return;

    for (const row of rows) {
      if (String(row.DNA || "").trim().toUpperCase() === normalized) {
        await dialog.warning(
          `DNA Number ${normalized} already exists. Please ensure you have a unique DNA number value.`
        );
        return;
      }
    }

    if (!activeCell || activeCell.rowIndex < 0 || activeCell.rowIndex >= rows.length) return;

    const source = rows[activeCell.rowIndex];
    const copy = { ...source, DNA: normalized };
    setRows((prev) => [...prev, copy]);
  }

  async function addChildPart() {
    const child = childInput.trim().toUpperCase();
    if (!child) return;

    const exists = await getPartExistsMajor(child);

    if (Number(exists) !== 0) {
      if (childParts.includes(child)) {
        await dialog.warning(
          "The child part that you have specified already exists in this list. Please double check your entry, and then try again. Thank you."
        );
      } else if (child === normalizedPartNum.trim().toUpperCase()) {
        await dialog.warning(
          "You cannot add a child part that is the same as the parent part. Operation Aborted."
        );
      } else {
        setChildParts((prev) => [...prev, child]);
        await dialog.success(`Child part ${child} has been successfully added.`);
        setChildInput("");
      }
    } else {
      await dialog.warning(
        "This part does not currently exist in the library of MAFIA tables. Please verify that this part exists before you can designate it as a child."
      );
    }
  }

  async function removeChildPart() {
    if (!childInput.trim()) return;
    const child = childInput.trim().toUpperCase();
    if (!childParts.includes(child)) return;

    const ans = await dialog.confirm({
      title: "Remove Child Part",
      message: `Are you sure you wish to remove child part ${child}?`,
      confirmText: "Remove",
      cancelText: "Cancel",
      type: "warning",
    });

    if (!ans) return;

    setChildParts((prev) => prev.filter((x) => x !== child));
    setChildInput("");
  }

  async function insertSymbol(symbolKind) {
    if (!activeCell) return;
    const { rowIndex, field } = activeCell;
    const row = rows[rowIndex];
    if (!row) return;

    const editableFields = ["AdditionalInfo", "SpecialInstructions", "WIATPNumber", "Gages", "Unit", "MOMOpNumber"];
    if (!editableFields.includes(field)) return;

    const key = `${rowIndex}-${field}`;
    const input = inputRefs.current[key];
    if (!input) return;

    const currentValue = row[field] || "";
    const start = input.selectionStart ?? currentValue.length;
    const end = input.selectionEnd ?? currentValue.length;

    const isReferenceStyle = String(row.ToleranceClass || "").toLowerCase().includes("reference");
    const mapping = getSymbolMapping(symbolKind, isNew, isReferenceStyle);
    if (!mapping.allowed) {
      await dialog.warning("This button is to provide a symbol for only 'Reference' type dimensions.");
      return;
    }

    const insertText = mapping.value;
    const next = currentValue.slice(0, start) + insertText + currentValue.slice(end);
    setRowValue(rowIndex, field, next);

    setTimeout(() => {
      const el = inputRefs.current[key];
      if (el) {
        el.focus();
        const pos = start + insertText.length;
        el.setSelectionRange(pos, pos);
      }
    }, 0);
  }

  async function validateDecimalFields(targetRows) {
    for (let j = 0; j < targetRows.length; j++) {
      const row = targetRows[j];
      for (const field of ["Max", "Min", "Nominal"]) {
        const val = String(row[field] ?? "").trim();
        if (isBlankNumeric(val)) continue;
        if (Number.isNaN(Number(val))) {
          const msg = `Non numeric ${field.toUpperCase()} value detected on row ${j + 1}. Please ensure Nominal, Min and Max values are either empty, or contain a numeric value only.`;
          setErrorText(msg);
          await dialog.warning(msg, "Invalid Numeric Value");
          return false;
        }
      }
    }
    return true;
  }

  async function validateRows(targetRows, fullValidation) {
    const dnaNumList = [];
    let problems = "";
    let opNumber = "";
    const dnaList = [];

    for (let j = 0; j < targetRows.length; j++) {
      const row = targetRows[j];
      const dna = String(row.DNA ?? "").trim();
      const dimType = String(row.DimensionType ?? "").trim();

      if (!dna || !dimType) {
        const msg = "Null values detected in DNA# or Dimension Type. Please make sure there are no empty rows in your MAFIA table and try again.";
        setErrorText(msg);
        await dialog.warning(msg);
        return { ok: false };
      }

      if (dnaNumList.includes(dna.toUpperCase())) {
        const msg = `There are duplicate DNA numbers in this MAFIA table. (DNA #${dna.toUpperCase()}.) Please correct the error and try again.`;
        setErrorText(msg);
        await dialog.warning(msg);
        return { ok: false };
      }
      dnaNumList.push(dna.toUpperCase());

      if (dna === "") {
        if (!problems.includes("Please ensure there is a DNA number for each dimension.")) {
          problems += " - Please ensure there is a DNA number for each dimension.\n";
        }
      } else if (dnaList.includes(dna.toLowerCase())) {
        problems += ` - Please ensure each DNA is a unique value. #${dna.toLowerCase()}\n`;
      } else {
        dnaList.push(dna.toLowerCase());
      }

      if (dimType === "") {
        if (!problems.includes(" - Please ensure there is a dimension type set for each dimension.")) {
          problems += " - Please ensure there is a dimension type set for each dimension.\n";
        }
      }

      if (!fullValidation) continue;

      const tolerance = String(row.ToleranceClass ?? "");
      const method = String(row.MethodOfRecording ?? "");
      const gage = String(row.Gages ?? "");
      const mom = String(row.MOMOpNumber ?? "");

      if (tolerance.trim() === "") {
        if (!dimType.toLowerCase().includes("note") && !dimType.toLowerCase().includes("basic") && !dimType.toLowerCase().includes("*")) {
          if (!problems.includes("Please ensure tolerance class is set for each dimension")) {
            problems += " - Please ensure tolerance class is set for each dimension. \n";
          }
        }
      }

      if (tolerance.trim() !== "" && !tolerance.toLowerCase().includes("reference") && !tolerance.toLowerCase().includes("basic")) {
        if (method.trim() === "") {
          if (!problems.includes(" - Please ensure each dimension has a 'Method of Recording' selected.")) {
            problems += " - Please ensure each dimension has a 'Method of Recording' selected.\n";
          }
        }

        if (!dimType.includes("*")) {
          if (gage.trim() === "") {
            if (!problems.includes(" - Please ensure a gage is selected for each dimension to be recorded.")) {
              problems += " - Please ensure a gage is selected for each dimension to be recorded.\n";
            }
          }
        }

        if (mom.trim() === "") {
          if (!opNumber.includes("MOM")) {
            opNumber += "MOM";
          }
        }
      }
    }

    if (problems !== "") {
      const msg = fullValidation
        ? `There is an error with this table. Please review the following error(s) before continuing.\n${problems}`
        : `There is an error with this table. Unfinished parts require at least a DNA Number and Dimension Type.\n${problems}`;
      setErrorText(msg);
      await dialog.warning(msg, "Validation Error");
      return { ok: false };
    }

    if (fullValidation && opNumber !== "") {
      const ans = await dialog.confirm({
        title: "Missing MOM Op #",
        message: "There is at least one DNA missing a MOM Op #, are you sure you wish to continue?",
        confirmText: "Continue",
        cancelText: "Cancel",
        type: "warning",
      });
      if (!ans) return { ok: false };
    }

    setErrorText("");
    return { ok: true };
  }

  async function persistRows({ targetPartNum, targetRevMaj, targetRevMin, historyToUse, createdByToUse, unfinished }) {
    for (let k = 0; k < rows.length; k++) {
      const row = rows[k];
      const payload = {
        Rev: targetRevMaj,
        RevMinor: targetRevMin,
        PartNum: stringOrEmpty(targetPartNum),
        DNANum: stringOrEmpty(row.DNA),
        DimType: stringOrEmpty(row.DimensionType),
        UnitMeasurement: stringOrEmpty(row.Unit),
        Nominal: stringOrEmpty(row.Nominal),
        Min: stringOrEmpty(row.Min),
        Max: stringOrEmpty(row.Max),
        ToleranceClass: stringOrEmpty(row.ToleranceClass),
        InProcess: stringOrEmpty(row.InProcess),
        MethodOfRecording: stringOrEmpty(row.MethodOfRecording),
        SpecialInstr: stringOrEmpty(row.SpecialInstructions),
        SpecialGages: stringOrEmpty(row.Gages),
        WIATPNum: stringOrEmpty(row.WIATPNumber),
        AddlInfo: stringOrEmpty(row.AdditionalInfo),
        MOMOperationNum:
          String(row.MOMOpNumber ?? "").trim() === ""
            ? null
            : Number.parseInt(String(row.MOMOpNumber).trim(), 10),
        createdBy: stringOrEmpty(createdByToUse),
        Stamp: new Date().toISOString(),
        History: stringOrEmpty(historyToUse),
      };

      // Round 5 fix (Batch 2): the add-line APIs THROW on a non-2xx response
      // (axios), so a 500 previously escaped persistRows entirely — crashing the
      // page via React's error overlay and never reaching the `if (!ok)` guard.
      // We now catch it, surface the ACTUAL server message (the endpoint returns
      // the SQL error text in the 500 body), and return false so callers can
      // stop cleanly without a false "success".
      let ok;
      try {
        ok = unfinished ? await addLineUnfinished(payload) : await addLine(payload);
      } catch (err) {
        const serverMsg =
          err?.response?.data && typeof err.response.data === "string"
            ? err.response.data
            : err?.response?.data?.message || err?.message || "Unknown server error";
        await dialog.error(
          `Could not save row (DNA "${stringOrEmpty(row.DNA) || "—"}") for part ${targetPartNum}. ` +
          `The server reported:\n\n${serverMsg}\n\n` +
          `No further rows were saved. Please note this message for technical support (x1776).`
        );
        return false;
      }
      if (!ok) {
        await dialog.error(
          `Error in adding Mafia Line to database. Please call technical support, x1776, PartNum = ${targetPartNum}`
        );
        return false;
      }
    }
    return true;
  }

  async function syncChildren(parentPartNum) {
    if (childParts.length > 0) {
      await deleteChild(parentPartNum);
      for (const child of childParts) {
        const ok = await addChildPartApi({ parentPart: parentPartNum, childPart: child });
        if (!ok) {
          await dialog.warning(
            "There seemed to be an error with the database and some (or all) of the children were not added. Please try again."
          );
        }
      }
    } else {
      await deleteChild(parentPartNum);
    }
  }

  function finalizeAndOpenFrmSelect(selectPartNum, selectRevMaj, selectRevMin, createdBy, allowKeepOpen = false) {
    // Go-live parity (2026-09-24, audit N5): legacy EditPart.vb:2005-2009,
    // 2450-2454 — after a dash-number or manual-rev save with "Keep page open"
    // ticked, the verifier selection opens ON TOP and this editor stays open
    // (with its rows) so the next dash child can be made straight away.
    if (allowKeepOpen && keepOpenAfterSave) {
      setKeepOpenSelect({ partnum: selectPartNum, revMajor: selectRevMaj, revMinor: selectRevMin, createdBy });
      return;
    }

    if (onOpenFrmSelect) {
      onOpenFrmSelect({ partnum: selectPartNum, revMajor: selectRevMaj, revMinor: selectRevMin, createdBy });
      return;
    }

    if (onDone) {
      onDone({ partnum: selectPartNum, revMajor: selectRevMaj, revMinor: selectRevMin, createdBy });
      return;
    }

    if (keepOpenAfterSave) {
      return;
    }

    if (onBack) onBack();
  }

  async function handleSaveAndClose() {
    if (!(await validateDecimalFields(rows))) return;
    const validation = await validateRows(rows, true);
    if (!validation.ok) return;

    let historyToUse = "";
    let createdByToUse = "";

    if (isImported) {
      historyToUse = "";
      createdByToUse = currentUsername.toLowerCase();
    } else if (isNew) {
      historyToUse = `(part created manually) ${currentUsername.toLowerCase()}`;
      createdByToUse = currentUsername.toLowerCase();
    } else {
      const oldHistory = await getOldHistory({ partNumber: normalizedPartNum, rev, revMinor: revMin });
      historyToUse = `${oldHistory}; (edit made) ${currentUsername.toLowerCase()}`;
      createdByToUse = await getCreatedBy({ partNumber: normalizedPartNum, rev, revMinor: revMin });
    }

    let targetRevMin = revMin;
    if (!isNew) targetRevMin = revMin + 1;

    const exists = await getPartExistsExact({ partNumber: normalizedPartNum, rev: revMaj, revMinor: targetRevMin });
    if (exists) {
      await dialog.warning(
        "Error -- this part already exists in the database. Someone must have created this part as the same time as you. Please save your work, investigate the issue, and come back when you are ready."
      );
      return;
    }

    const ok = await persistRows({
      targetPartNum: normalizedPartNum,
      targetRevMaj: revMaj,
      targetRevMin,
      historyToUse,
      createdByToUse,
      unfinished: false,
    });
    if (!ok) return;

    setSubmitted(true);
    await syncChildren(normalizedPartNum);

    const revisedByNow = await getRevisedBy({ partNumber: normalizedPartNum, rev, revMinor });
    if (currentUsername.toLowerCase().trim() !== revisedByNow.toLowerCase().trim()) {
      // Legacy parity (EditPart.vb Button6_Click, lines 1571-1572): when the
      // person saving is NOT the last reviser, notify by email. This call was
      // previously commented out — a real gap vs legacy (Manual Rev Change did
      // send it, Save & Close didn't). Restored. Soft-fails so a mail hiccup
      // never blocks the save.
      try {
        await sendEmailPartEdited({ fromName: currentUsername.toLowerCase(), partNum: normalizedPartNum, revMajor: rev, revMinor });
      } catch (err) {
        console.error("Save & Close: part-edited email failed (non-blocking)", err);
      }
    }

    if (isImported) {
      await deleteMafiaTable(normalizedPartNum, revMaj, revMin);
      await updateRevMinor({ partNumber: normalizedPartNum, rev: revMaj, revMinor: revMin });
      targetRevMin = revMin;
    }

    finalizeAndOpenFrmSelect(normalizedPartNum.trim().toUpperCase(), revMaj, targetRevMin, currentUsername.toLowerCase());
  }

  async function handleSaveDashChild() {
    if (!partText.includes("-") || partText.trim().toUpperCase() === normalizedPartNum.trim().toUpperCase()) {
      await dialog.warning(
        "This button is for only saving a part with a dash number. Please use the 'Save and Close' button to continue without a dash number."
      );
      return;
    }

    let newPartNum = partText.trim();
    if (newPartNum.endsWith("-")) {
      newPartNum = newPartNum.slice(0, -1);
      setPartText(newPartNum);
    }

    if (!(await validateDecimalFields(rows))) return;
    const validation = await validateRows(rows, true);
    if (!validation.ok) return;

    const latestMajor = await getLatestMajorRev(newPartNum);
    const newRevMaj = Number(latestMajor) + 1;
    const latestMinor = await getLatestRevMinor({ partNum: newPartNum, rev: newRevMaj });
    const newRevMin = Number(latestMinor) + 1;

    const oldHistory = await getOldHistory({ partNumber: normalizedPartNum, rev, revMinor: revMin });
    const historyToUse = `${oldHistory}; (child created) ${currentUsername.toLowerCase()}`;
    const createdByToUse = await getCreatedBy({ partNumber: normalizedPartNum, rev, revMinor: revMin });

    const ok = await persistRows({
      targetPartNum: newPartNum,
      targetRevMaj: newRevMaj,
      targetRevMin: newRevMin,
      historyToUse,
      createdByToUse,
      unfinished: false,
    });
    if (!ok) return;

    setSubmitted(true);
    await syncChildren(newPartNum);
    finalizeAndOpenFrmSelect(newPartNum.trim().toUpperCase(), newRevMaj, newRevMin, createdByToUse, true);
  }

  async function handleManualRevChange() {
    const newRevMaj = Number.parseInt(manualRevMaj.trim(), 10);
    const newRevMin = Number.parseInt(manualRevMin.trim(), 10);

    if (Number.isNaN(newRevMaj) || Number.isNaN(newRevMin)) {
      await dialog.warning("Invalid Rev # detected, please try again.");
      return;
    }

    const tester = await getPart({ partNumber: normalizedPartNum, rev: newRevMaj, revMinor: newRevMin });
    if ((tester || []).length > 0) {
      await dialog.warning("A MAFIA table with the specified part and Rev #'s already exists. Operation aborted.");
      return;
    }

    if (!(await validateDecimalFields(rows))) return;
    const validation = await validateRows(rows, true);
    if (!validation.ok) return;

    const oldHistory = await getOldHistory({ partNumber: normalizedPartNum, rev, revMinor: revMin });
    const historyToUse = `${oldHistory}; (manual rev# change from ${revMaj}-${revMin} to ${newRevMaj}-${newRevMin}) ${currentUsername.toLowerCase()}`;
    const createdByToUse = await getCreatedBy({ partNumber: normalizedPartNum, rev, revMinor: revMin });

    const ok = await persistRows({
      targetPartNum: normalizedPartNum,
      targetRevMaj: newRevMaj,
      targetRevMin: newRevMin,
      historyToUse,
      createdByToUse,
      unfinished: false,
    });
    if (!ok) return;

    setSubmitted(true);
    await syncChildren(normalizedPartNum);

    const revisedByNow = await getRevisedBy({ partNumber: normalizedPartNum, rev, revMinor });
    if (currentUsername.toLowerCase().trim() !== revisedByNow.toLowerCase().trim()) {
      await sendEmailPartEdited({ fromName: currentUsername.toLowerCase(), partNum: normalizedPartNum, revMajor: rev, revMinor });
    }

    finalizeAndOpenFrmSelect(normalizedPartNum.trim().toUpperCase(), newRevMaj, newRevMin, createdByToUse, true);
  }

  // Round 5 (Batch 2): Save Unfinished is the "park my draft and keep working"
  // button — the deliberate opposite of Save & Close (which finalizes at rev+1
  // and leaves). Per the approved decision:
  //   * Saves the current rows as an in-progress DRAFT at the SAME rev (NOT
  //     rev+1). Save & Close is the one that bumps the rev.
  //   * De-dupes: deletes the existing rows for this exact (part, rev, revMinor)
  //     BEFORE re-inserting, so clicking Save Unfinished repeatedly no longer
  //     piles up duplicate rows (the insert path is append-only).
  //   * Does NOT call onDone (onDone navigates back to the menu — that was the
  //     "my part disappeared" bug). It stays on the Edit Part page.
  //   * Keeps the lock held by the current user and reloads the saved rows so
  //     the page reflects exactly what's in the database.
  //   * Lenient validation (DNA + Dimension Type) is preserved — you can save an
  //     incomplete draft. Full validation still runs for Save & Close/Confirm.
  //
  // Go-live parity (2026-09-24): the same-rev overwrite is used ONLY when the
  // opened revision is already a draft (InProgress = 1), is the import working
  // copy, or has no saved rows yet (brand-new part). Any other revision
  // (verified, released or awaiting verification) is left untouched: like
  // legacy EditPart.vb Button7 (2784-2984) the draft is saved as a NEW
  // revision at MAFIA rev + 1 with InProgress = 1 and nothing is deleted.
  // Before, the opened revision was deleted and re-inserted as a draft, which
  // silently un-released it and wiped its history and author.
  async function handleSaveUnfinished() {
    // Go-live parity (2026-09-24): Nominal/Min/Max must be numeric, blank or
    // "-" (legacy converted them with CDec and stopped on anything else).
    if (!(await validateDecimalFields(rows))) return;
    const validation = await validateRows(rows, false);
    if (!validation.ok) return;

    let overwriteSameRev;
    try {
      const [openedExists, openedIsDraft] = await Promise.all([
        getPartExistsExact({ partNumber: normalizedPartNum, rev: revMaj, revMinor: revMin }),
        getInProgress({ partNumber: normalizedPartNum, rev: revMaj, revMinor: revMin }),
      ]);
      overwriteSameRev = isImported || !openedExists || openedIsDraft;
    } catch (err) {
      console.error("Save Unfinished: could not read the revision status", err);
      await dialog.error(
        "Could not check the status of this revision. Your on-screen work has NOT been lost. Please try again or call technical support, x1776."
      );
      return;
    }

    if (!overwriteSameRev) {
      await saveUnfinishedAsNewDraft();
      return;
    }

    // De-dupe: remove any existing rows for this exact part+rev before we
    // re-insert the current editor state. Best-effort — if the part has no rows
    // yet (brand-new), the delete is a harmless no-op on the backend.
    try {
      await deleteMafiaTable(normalizedPartNum, revMaj, revMin);
    } catch (err) {
      console.error("Save Unfinished: pre-insert cleanup failed", err);
      await dialog.error(
        "Could not prepare the draft for saving (cleanup step failed). Your on-screen work has NOT been lost. Please try again or call technical support, x1776."
      );
      return;
    }

    // Re-insert current rows at the SAME rev as an in-progress draft.
    const ok = await persistRows({
      targetPartNum: normalizedPartNum,
      targetRevMaj: revMaj,
      targetRevMin: revMin, // SAME rev — not revMin + 1
      historyToUse: "",
      createdByToUse: currentUsername.toLowerCase(),
      unfinished: true,
    });
    if (!ok) {
      // persistRows already surfaced the specific error.
      return;
    }

    // Keep the lock held by this user (defensive re-assert; loadPage set it, but
    // a long editing session could have had it cleared elsewhere).
    try {
      await setLockStatus({
        partNum: normalizedPartNum,
        rev: revMaj,
        revMinor: revMin,
        newLock: currentUsername.toLowerCase(),
      });
    } catch (err) {
      console.error("Save Unfinished: could not re-assert lock", err);
    }

    await dialog.success(
      "Draft saved. This part is now in the In Progress queue; you can keep editing."
    );

    // Stay on the page and refresh from the database so the editor shows exactly
    // what was saved (and the Created/Edited timeline updates). We do NOT call
    // onDone here — that is what navigated away and made the part look lost.
    await loadPage();
  }

  // Go-live parity (2026-09-24): Save Unfinished on a finished revision —
  // legacy EditPart.vb Button7: the draft goes to MAFIA rev + 1 with
  // InProgress = 1 and the opened revision is not deleted or changed.
  async function saveUnfinishedAsNewDraft() {
    const draftRevMin = Number(revMin) + 1;

    let exists;
    try {
      exists = await getPartExistsExact({ partNumber: normalizedPartNum, rev: revMaj, revMinor: draftRevMin });
    } catch (err) {
      console.error("Save Unfinished: could not check the draft revision", err);
      await dialog.error(
        "Could not check the status of this revision. Your on-screen work has NOT been lost. Please try again or call technical support, x1776."
      );
      return;
    }
    if (exists) {
      await dialog.warning(
        `MAFIA revision ${revMaj}.${draftRevMin} of this part already exists, so the draft cannot be saved there. Nothing was saved or changed. Please open the latest revision of this part and try again.`
      );
      return;
    }

    const ok = await persistRows({
      targetPartNum: normalizedPartNum,
      targetRevMaj: revMaj,
      targetRevMin: draftRevMin,
      historyToUse: "",
      createdByToUse: currentUsername.toLowerCase(),
      unfinished: true,
    });
    if (!ok) {
      // persistRows already surfaced the specific error.
      return;
    }

    await dialog.success(
      `Draft saved as MAFIA revision ${revMaj}.${draftRevMin} (In Progress queue). Revision ${revMaj}.${revMin} was not changed. You can keep editing the draft.`
    );

    // Keep working on the NEW draft (Save Unfinished stays open): re-open the
    // editor on it, which releases this revision's lock and locks the draft.
    if (onOpenRevision) {
      onOpenRevision({ partNum: normalizedPartNum, rev: revMaj, revMinor: draftRevMin });
    } else if (onBack) {
      onBack();
    }
  }

  // Mirrors legacy EditPart_FormClosing (EditPart.vb lines 47-76):
  //   - If user is in the middle of an Import (isImported && !submitted):
  //     prompt to confirm; on confirm, delete the temp table so a re-import
  //     is needed next time; on cancel, stay on the page.
  //   - Otherwise just go back.
  //
  // The window 'beforeunload' handler covers browser close. This covers the
  // in-app Back button.
  async function handleBack() {
    if (isImported && !submitted) {
      const ok = await dialog.confirm({
        title: "Discard import?",
        message:
          "You have not completed the import. Any existing work will be lost and a re-import will be required in the future. Are you sure you wish to continue?",
        confirmText: "Discard",
        cancelText: "Stay",
      });
      if (!ok) return;
      try {
        await deleteMafiaTable(normalizedPartNum, rev, revMinor);
      } catch (err) {
        // Best-effort cleanup; the orphan table won't crash anything, just
        // leaves stale data that a future re-import will overwrite.
        console.error("Failed to delete temp import table on back:", err);
      }
    }
    if (onBack) onBack();
  }

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardBody className="p-4 text-muted">Loading Edit Part workspace...</CardBody>
      </Card>
    );
  }

  return (
    <div className={`edit-part-viewport mafia-screen${compact ? " edit-part-compact" : ""}`}>
      <MafiaTheme />
      <style>{EDIT_PART_UI_CSS}</style>
      <MafiaPageHeader
        compact
        icon="ri-edit-box-line"
        eyebrow="MAFIA Aux"
        title={`Edit Part — ${normalizedPartNum || "—"}`}
        subtitle={
          <>
            Rev {revMaj}.{revMin} · {rows.length} row{rows.length === 1 ? "" : "s"}
            {(lockOwner || currentUsername) && (
              <>
                {" · Locked by "}
                <strong style={{ color: "rgba(255,255,255,0.95)" }}>
                  {lockOwnerDisplay || currentUserDisplay || lockOwner || currentUsername}
                </strong>
              </>
            )}
          </>
        }
        tone="violet"
        actions={
          <>
            <div style={{ minWidth: 240, maxWidth: 320 }}>
              <InputGroup size="sm">
                <InputGroupText
                  className="border-0"
                  style={{
                    background: "rgba(255,255,255,0.18)",
                    color: "#fff",
                  }}
                >
                  <i className="ri-search-line" />
                </InputGroupText>
                <Input
                  value={tableSearch}
                  onChange={(e) => setTableSearch(e.target.value)}
                  placeholder="Search DNA, dimension, gage…"
                  style={{
                    background: "rgba(255,255,255,0.92)",
                    border: "none",
                  }}
                />
              </InputGroup>
            </div>
            <Button
              color="light"
              onClick={handleBack}
              style={{
                background: "rgba(255,255,255,0.92)",
                border: "none",
                fontWeight: 500,
              }}
            >
              <i className="ri-arrow-left-line align-bottom me-1" />
              Back
            </Button>
          </>
        }
        breadcrumb={[
          { label: "Applications", to: "/" },
          { label: "MAFIA Aux", onClick: onBack },
          { label: normalizedPartNum ? `Edit Part ${normalizedPartNum}` : "Edit Part" },
        ]}
      />

      {loadError && (
        <div className="alert alert-danger d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3 mb-0" role="alert">
          <span>
            <i className="ri-error-warning-line me-1" />
            {loadError}
          </span>
          <button type="button" className="btn btn-sm btn-danger" onClick={() => loadPage()}>
            <i className="ri-refresh-line me-1" />
            Retry
          </button>
        </div>
      )}

      {/* Status timeline (derived from the preserved history chain) */}
      <MafiaStatusTimeline
        history={history}
        createdByLabel={createdByDisplay || createdByLabel}
        current="editing"
        isImported={isImported}
      />

      {/* Action toolbar */}
      <Card className="border-0 shadow-sm mb-3">
        <CardBody className="py-2 px-3">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Button size="sm" color="primary" onClick={handleSaveAndClose}>
              <i className="ri-save-line align-bottom me-1" />Save &amp; Close
            </Button>
            <Button size="sm" color="light" className="border" onClick={handleSaveUnfinished}>
              <i className="ri-draft-line align-bottom me-1" />Save Unfinished
            </Button>
            {!isImported && (
              <>
                <Button size="sm" color="light" className="border" onClick={handleManualRevChange}>
                  <i className="ri-git-branch-line align-bottom me-1" />Manual Rev Change Save
                </Button>
                <Button size="sm" color="light" className="border" onClick={handleSaveDashChild}>
                  <i className="ri-node-tree align-bottom me-1" />Save Dash / Child Part
                </Button>
              </>
            )}

            <div className="vr mx-1 d-none d-md-inline-block" />

            <Button size="sm" color="success" outline onClick={addBlankRow}>
              <i className="ri-add-line align-bottom me-1" />Add Row
            </Button>
            <Button size="sm" color="info" outline onClick={copyCurrentRowToNewDNA}>
              <i className="ri-file-copy-line align-bottom me-1" />Copy Row
            </Button>
            <Button size="sm" color="danger" outline onClick={deleteCurrentRow} disabled={!activeCell}>
              <i className="ri-delete-bin-6-line align-bottom me-1" />Delete Current Row
            </Button>
            {activeCell && (
              <Badge color="warning-subtle" className="text-warning ms-1 fw-medium px-2 py-1">
                Selected cell: {selectedColumn || "—"}
              </Badge>
            )}

            <div className="ms-auto d-flex align-items-center gap-2">
              {/* Round 66: pin any column from its header (pin icon). This
                  shows how many are pinned and clears them all in one click. */}
              <span className="text-muted small" title="Use the pin icon in any column header to freeze that column">
                <i className="ri-pushpin-2-line me-1" />
                {pinnedKeys.length > 0 ? `${pinnedKeys.length} pinned` : "Pin columns via header icons"}
              </span>
              {pinnedKeys.length > 0 && (
                <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={clearPins}>
                  Unpin all
                </button>
              )}
              <span className="text-muted small ms-3">Compact</span>
              <button
                type="button"
                className={`epx-toggle${compact ? " on" : ""}`}
                aria-pressed={compact}
                title="Toggle row density"
                onClick={() => setCompact((v) => !v)}
              />
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Advisory validation summary (never blocks Save) */}
      {validationSummary.fields.length > 0 && (
        <div className="epx-vbar mb-3">
          <i className="ri-error-warning-line" />
          <span>
            <strong>{validationSummary.rowsWithIssues}</strong> row
            {validationSummary.rowsWithIssues === 1 ? "" : "s"} need attention before this part is confirmed:
          </span>
          <span className="epx-vlist">{validationSummary.fields.join(" · ")}</span>
        </div>
      )}

      {/* Main data table */}
      <Card className="border-0 shadow-sm mb-3">
        <CardBody className="p-0">
          <div className="edit-part-table-scroll epx-gridwrap">
            <Table responsive={false} className={`align-middle table-hover mb-0 epx-grid${pinnedKeys.length > 0 ? " epx-pin" : ""}`}>
              <thead className="table-light">
                <tr>
                  <th className="epx-sticky-num" style={{ width: 50 }}>#</th>
                  {COLUMN_DEFS.map((col) => {
                    const info = pinInfo.get(col.key);
                    return (
                      <th
                        key={col.key}
                        className={info ? `epx-pinned-th${info.last ? " epx-pin-last" : ""}` : ""}
                        style={info ? { ...pinStyleFor(col.key) } : { minWidth: col.width }}
                      >
                        <span className="epx-thwrap">
                          <span className="epx-thlabel" title={col.label}>{col.label}</span>
                          <button
                            type="button"
                            className={`epx-pinbtn${info ? " on" : ""}`}
                            title={info ? "Unpin column" : "Pin column (freeze while scrolling)"}
                            aria-pressed={!!info}
                            onClick={(e) => { e.stopPropagation(); togglePin(col.key); }}
                          >
                            <i className={info ? "ri-pushpin-2-fill" : "ri-pushpin-line"} />
                          </button>
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredRowEntries.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMN_DEFS.length + 1} className="text-center py-5 text-muted">
                      No rows match your search.
                    </td>
                  </tr>
                ) : (
                  filteredRowEntries.map(({ row, index: originalIndex }, filteredIndex) => {
                    const isSelected = activeCell?.rowIndex === originalIndex;
                    return (
                      <tr
                        // Round 4 (Batch 2): key is the STABLE original row index
                        // only. It previously included `row.DNA`, so typing in
                        // the DNA cell changed the key and React unmounted +
                        // remounted the entire <tr> on every keystroke — the
                        // root cause of the DNA typing freeze. Index is stable
                        // across edits (rows aren't reordered while typing).
                        key={originalIndex}
                        ref={(el) => { rowRefs.current[originalIndex] = el; }}
                        className={isSelected ? "table-active" : ""}
                        onClick={() =>
                          setActiveCell({
                            rowIndex: originalIndex,
                            field: activeCell?.rowIndex === originalIndex ? activeCell.field : "DNA",
                          })
                        }
                      >
                        <td className="text-center epx-sticky-num">
                          <div className="epx-numcell">
                            <Input
                              type="radio"
                              checked={isSelected}
                              onChange={() => setActiveCell({ rowIndex: originalIndex, field: "DNA" })}
                              name="selected-edit-part-row"
                            />
                            <div className="epx-rowact">
                              <button
                                type="button"
                                className="epx-icobtn"
                                title="Copy this row"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveCell({ rowIndex: originalIndex, field: "DNA" });
                                  copyCurrentRowToNewDNA();
                                }}
                              >
                                <i className="ri-file-copy-line" />
                              </button>
                              <button
                                type="button"
                                className="epx-icobtn del"
                                title="Delete this row"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveCell({ rowIndex: originalIndex, field: "DNA" });
                                  deleteCurrentRow();
                                }}
                              >
                                <i className="ri-delete-bin-6-line" />
                              </button>
                            </div>
                          </div>
                        </td>
                        {COLUMN_DEFS.map((col) => (
                          <EditableCell
                            key={col.key}
                            rowIndex={originalIndex}
                            row={row}
                            column={col}
                            gageOptions={gageOptions}
                            onChange={setRowValue}
                            setActiveCell={setActiveCell}
                            inputRefs={inputRefs}
                            invalid={rowErrors[originalIndex]?.has(col.key) || false}
                            pinStyle={pinStyleFor(col.key)}
                            pinLast={!!pinInfo.get(col.key)?.last}
                            className={col.key === "AdditionalInfo" ? getAdditionalInfoClass(row) : ""}
                          />
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

      {/* Lower panels: Part Controls / Manual Rev / Children / Symbols */}
      <Row className="g-3">
        <Col xl={4} md={6}>
          <Card className="border-0 shadow-sm h-100">
            <CardBody>
              <h6 className="text-muted text-uppercase fs-12 mb-3">Part Controls</h6>
              <div className="mb-3">
                <Label className="form-label">Part Number</Label>
                <Input value={partText} onChange={(e) => setPartText(e.target.value)} />
              </div>
              <div className="mb-3">
                <Label className="form-label">Determine In Process Quantity</Label>
                <Input value={qtyForSampling} onChange={(e) => setQtyForSampling(e.target.value)} />
              </div>
              <Button color="success" className="w-100" onClick={determineInProcess}>
                <i className="ri-calculator-line align-bottom me-1" />Determine In Process
              </Button>
            </CardBody>
          </Card>
        </Col>

        {!isImported && (
          <Col xl={4} md={6}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <h6 className="text-muted text-uppercase fs-12 mb-3">Manual Rev Change</h6>
                <Row className="g-2">
                  <Col xs={6}>
                    {/* Round 7 (Batch 3): UI label only. DB column is still `Rev`. */}
                    <Label className="form-label">Part Revision</Label>
                    <Input value={manualRevMaj} onChange={(e) => setManualRevMaj(e.target.value)} />
                  </Col>
                  <Col xs={6}>
                    {/* Round 7 (Batch 3): UI label only. DB column is still `RevMinor`. */}
                    <Label className="form-label">MAFIA Revision</Label>
                    <Input value={manualRevMin} onChange={(e) => setManualRevMin(e.target.value)} />
                  </Col>
                </Row>
                <div className="form-check mt-3">
                  <Input
                    id="keepOpenAfterSave"
                    type="checkbox"
                    checked={keepOpenAfterSave}
                    onChange={(e) => setKeepOpenAfterSave(e.target.checked)}
                  />
                  <Label htmlFor="keepOpenAfterSave" className="form-check-label">
                    Keep page open after save
                  </Label>
                </div>
              </CardBody>
            </Card>
          </Col>
        )}

        <Col xl={4} md={6}>
          <Card className="border-0 shadow-sm h-100">
            <CardBody>
              <h6 className="text-muted text-uppercase fs-12 mb-3">Children</h6>
              <div className="mb-2">
                <Label className="form-label">Child Part</Label>
                <Input value={childInput} onChange={(e) => setChildInput(e.target.value)} />
              </div>
              <div className="d-flex gap-2 mb-3">
                <Button size="sm" color="primary" className="flex-fill" onClick={addChildPart}>Add</Button>
                <Button size="sm" color="danger" outline className="flex-fill" onClick={removeChildPart}>Remove</Button>
              </div>
              <div className="d-flex flex-wrap gap-1">
                {childParts.length === 0 ? (
                  <div className="text-muted small">No child parts.</div>
                ) : (
                  childParts.map((child) => (
                    <Badge key={child} color="light" className="text-dark border fw-medium">
                      {child}
                    </Badge>
                  ))
                )}
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xl={12}>
          <Card className="border-0 shadow-sm">
            <CardBody>
              <h6 className="text-muted text-uppercase fs-12 mb-3">Symbols</h6>
              <div className="d-flex flex-wrap gap-2">
                <SymbolButton label="/" onClick={() => insertSymbol("slash")} />
                <SymbolButton label="(" onClick={() => insertSymbol("openParen")} />
                <SymbolButton label=")" onClick={() => insertSymbol("closeParen")} />
                <SymbolButton label="{" onClick={() => insertSymbol("openBrace")} />
                <SymbolButton label="}" onClick={() => insertSymbol("closeBrace")} />
                <SymbolButton label="<" onClick={() => insertSymbol("lessThan")} />
                <SymbolButton label=">" onClick={() => insertSymbol("greaterThan")} />
                <SymbolButton label="±" onClick={() => insertSymbol("plusMinus")} />
                <SymbolButton label="°" onClick={() => insertSymbol("degree")} />
              </div>
              <p className="text-muted small mt-2 mb-0">
                Symbols are inserted into the currently selected editable field.
              </p>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <style>{`
        /* The page sits inside its own viewport-height container with its
           own internal scroll. This bypasses the dashboard's outer page
           scroll which has rendering issues from upstream layout components.
           User scrolls inside .edit-part-viewport, not the browser window. */
        .edit-part-viewport {
          height: calc(100vh - 180px);
          overflow-y: auto;
          overflow-x: hidden;
          padding-right: 6px;
          contain: layout style paint;
        }
        .edit-part-table-scroll {
          max-height: 480px;
          min-height: 280px;
          overflow: auto;
          contain: layout style paint;
          will-change: transform;
          transform: translateZ(0);
        }
        .edit-part-table-scroll thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: var(--ssw-surface-3, #f3f6f9);
        }
        .edit-part-table-scroll input.form-control,
        .edit-part-table-scroll select.form-select {
          min-width: 90px;
        }
        .edit-part-additional-reference { background-color: var(--ssw-tint-yellow, #fff3cd); }
        .edit-part-additional-note      { background-color: var(--ssw-tint-blue, #e7f5ff); }
        .edit-part-additional-gdt       { background-color: var(--ssw-tint-purple, #f3e8ff); }
        .edit-part-additional-default   { background-color: transparent; }
      `}</style>

      {/* Go-live parity (2026-09-24, audit N5): "Keep page open after save". */}
      <Modal isOpen={!!keepOpenSelect} size="xl" centered toggle={() => setKeepOpenSelect(null)}>
        <ModalBody className="p-3">
          {keepOpenSelect ? (
            <FrmSelect
              partnum={keepOpenSelect.partnum}
              revMajor={keepOpenSelect.revMajor}
              revMinor={keepOpenSelect.revMinor}
              createdBy={keepOpenSelect.createdBy}
              onBack={() => setKeepOpenSelect(null)}
              onFinish={() => setKeepOpenSelect(null)}
            />
          ) : null}
        </ModalBody>
      </Modal>
    </div>
  );
}

// Round 4 (Batch 2): EditableCell is wrapped in React.memo. Combined with the
// stable row key and stable onChange/setActiveCell (useCallback/setState) refs,
// a keystroke in one cell now re-renders ONLY that cell instead of all ~40 rows
// × ~15 columns. The custom comparator re-renders a cell only when something it
// actually shows changes: its own value, its invalid flag, its className, or
// (for gage datalists) the gage option list. rowIndex/column are stable.
const EditableCell = React.memo(function EditableCell({ rowIndex, row, column, gageOptions, onChange, setActiveCell, inputRefs, className = "", invalid = false, pinStyle = null, pinLast = false }) {
  const baseCls = column.type === "select" ? "form-select form-select-sm" : "form-control form-control-sm";
  const commonProps = {
    onFocus: () => setActiveCell({ rowIndex, field: column.key }),
    className: `${baseCls} ${className}`.trim(),
  };
  const tdClass = `${invalid ? "epx-invalid" : ""} ${pinStyle ? `epx-pinned-td${pinLast ? " epx-pin-last" : ""}` : ""}`.trim();
  const tdStyle = pinStyle || undefined;
  const errDot = invalid ? <span className="epx-errdot" /> : null;

  if (column.type === "select") {
    return (
      <td className={tdClass} style={tdStyle}>
        {errDot}
        <select
          value={row[column.key] ?? ""}
          onChange={(e) => onChange(rowIndex, column.key, e.target.value)}
          {...commonProps}
        >
          <option value=""></option>
          {column.options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </td>
    );
  }

  if (column.type === "datalist") {
    const listId = `gage-list-${rowIndex}`;
    const key = `${rowIndex}-${column.key}`;
    return (
      <td className={tdClass} style={tdStyle}>
        {errDot}
        <input
          ref={(el) => { inputRefs.current[key] = el; }}
          list={listId}
          type="text"
          value={row[column.key] ?? ""}
          onChange={(e) => onChange(rowIndex, column.key, e.target.value)}
          {...commonProps}
        />
        <datalist id={listId}>
          {gageOptions.map((opt) => <option key={opt} value={opt} />)}
        </datalist>
      </td>
    );
  }

  if (column.type === "longtext") {
    // Round 65 (UAT): long sentences in Additional Info / Special
    // Instructions were unreadable in the small cell. The cell now expands
    // into a large floating text box while it has focus (type comfortably,
    // see the whole sentence) and collapses back when you click away. Same
    // value/handlers as a normal cell — nothing changes about saving.
    const key = `${rowIndex}-${column.key}`;
    return (
      <td className={`${tdClass} epx-ltd`.trim()} style={tdStyle}>
        {errDot}
        <textarea
          ref={(el) => { inputRefs.current[key] = el; }}
          rows={1}
          value={row[column.key] ?? ""}
          onChange={(e) => onChange(rowIndex, column.key, e.target.value)}
          {...commonProps}
          className={`${commonProps.className} epx-longtext`.trim()}
        />
      </td>
    );
  }

  const key = `${rowIndex}-${column.key}`;
  return (
    <td className={tdClass} style={tdStyle}>
      {errDot}
      <input
        ref={(el) => { inputRefs.current[key] = el; }}
        type="text"
        value={row[column.key] ?? ""}
        onChange={(e) => onChange(rowIndex, column.key, e.target.value)}
        {...commonProps}
      />
    </td>
  );
}, (prev, next) => {
  // Re-render only when something this cell actually renders changes. NOTE we
  // compare the cell's OWN value (row[column.key]) — NOT the row object ref,
  // which changes on every edit to any sibling cell. rowIndex/column/onChange/
  // setActiveCell/inputRefs are stable across renders.
  if (prev.column.key !== next.column.key) return false;
  if (prev.rowIndex !== next.rowIndex) return false;
  if ((prev.row?.[prev.column.key] ?? "") !== (next.row?.[next.column.key] ?? "")) return false;
  if (prev.invalid !== next.invalid) return false;
  if (prev.className !== next.className) return false;
  if ((prev.pinStyle?.left ?? -1) !== (next.pinStyle?.left ?? -1)) return false;
  if (prev.pinLast !== next.pinLast) return false;
  // Gage datalist options only matter for the datalist column.
  if (next.column.type === "datalist" && prev.gageOptions !== next.gageOptions) return false;
  return true; // props equal -> skip re-render
});

// Scoped styles for the Edit Part redesign. Uses the existing Velzon palette
// (indigo/teal) and stays inside this screen — no global/shared style changes.
const EDIT_PART_UI_CSS = `
.edit-part-viewport{ --epx-row-py:10px; --epx-cell-fs:13px; }
.edit-part-viewport.edit-part-compact{ --epx-row-py:3px; --epx-cell-fs:12px; }

/* density-driven cell sizing */
.edit-part-viewport .epx-grid tbody td{ padding-top:var(--epx-row-py); padding-bottom:var(--epx-row-py); }
.edit-part-viewport .epx-grid tbody td .form-control,
.edit-part-viewport .epx-grid tbody td .form-select{ font-size:var(--epx-cell-fs); }

/* sticky header + sticky DNA/# columns */
.edit-part-viewport .epx-gridwrap{ overflow:auto; max-height:62vh; }
.edit-part-viewport .epx-grid thead th{ position:sticky; top:0; z-index:5; background: var(--ssw-surface-3, #f3f4f9); }
/* Round 66: per-column pinning. The # gutter freezes whenever any column is
   pinned (.epx-pin scope); each user-pinned column carries inline left/width
   and these classes for layer/background/hover. */
.edit-part-viewport .epx-grid.epx-pin th.epx-sticky-num,
.edit-part-viewport .epx-grid.epx-pin td.epx-sticky-num{ position:sticky; left:0; z-index:6; background: var(--ssw-surface-3, #f3f4f9); }
.edit-part-viewport .epx-grid.epx-pin td.epx-sticky-num{ background: var(--ssw-surface, #fff); z-index:4; }
.edit-part-viewport .epx-grid th.epx-pinned-th{ position:sticky; z-index:7; background: var(--ssw-surface-3, #f3f4f9); }
.edit-part-viewport .epx-grid td.epx-pinned-td{ position:sticky; z-index:4; background: var(--ssw-surface, #fff); }
.edit-part-viewport .epx-grid th.epx-pin-last,
.edit-part-viewport .epx-grid td.epx-pin-last{ box-shadow:2px 0 0 #e9ebf0; }
.edit-part-viewport .epx-grid.epx-pin tbody tr:hover td.epx-sticky-num,
.edit-part-viewport .epx-grid tbody tr:hover td.epx-pinned-td{ background: var(--ssw-surface-3, #f5f6ff); }

/* header pin buttons */
.edit-part-viewport .epx-thwrap{ display:flex; align-items:center; gap:4px; }
.edit-part-viewport .epx-thlabel{ overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.edit-part-viewport .epx-pinbtn{ border:none; background:transparent; padding:0 2px; color: var(--ssw-fg-3, #b6bbc7); cursor:pointer; font-size:12px; line-height:1; flex:0 0 auto; }
.edit-part-viewport .epx-grid thead th:hover .epx-pinbtn{ color: var(--ssw-fg-2, #7b8190); }
.edit-part-viewport .epx-pinbtn.on{ color: var(--ssw-ink-indigo, #6d5fda); }
.edit-part-viewport .epx-pinbtn:hover{ color: var(--ssw-ink-indigo, #6d5fda); }

/* Round 65: expanding long-text editor (Additional Info / Special Instr.) */
.edit-part-viewport .epx-grid td.epx-ltd{ position:relative; }
.edit-part-viewport .epx-grid td .epx-longtext{
  height:31px; min-height:31px; resize:none; overflow:hidden;
  white-space:nowrap; line-height:1.4;
}
.edit-part-viewport .epx-grid td .epx-longtext:focus{
  position:absolute; top:2px; left:2px; width:460px; max-width:70vw;
  height:140px; overflow:auto; white-space:pre-wrap; z-index:30;
  background: var(--ssw-surface, #fff); box-shadow:0 12px 28px rgba(31,39,51,.22);
  border-color:#6d5fda;
}

/* inline validation */
.edit-part-viewport .epx-grid td.epx-invalid{ position:relative; }
.edit-part-viewport .epx-grid td.epx-invalid .form-control,
.edit-part-viewport .epx-grid td.epx-invalid .form-select{ border-color:#f06548; background: var(--ssw-surface-3, #fff5f3); }
.edit-part-viewport .epx-errdot{ position:absolute; top:4px; right:5px; width:7px; height:7px; border-radius:50%; background:#f06548; z-index:2; }

/* row hover actions in the # gutter */
.edit-part-viewport .epx-numcell{ position:relative; display:flex; align-items:center; justify-content:center; }
.edit-part-viewport .epx-rowact{ position:absolute; top:100%; left:50%; transform:translateX(-50%); display:flex; gap:3px; opacity:0; transition:opacity .1s; padding-top:2px; }
.edit-part-viewport .epx-grid tbody tr:hover .epx-rowact{ opacity:1; }
.edit-part-viewport .epx-icobtn{ border:none; background: var(--ssw-surface-3, #f0f1f6); border-radius:6px; width:22px; height:22px; cursor:pointer; color: var(--ssw-fg-2, #7b8190); font-size:12px; line-height:1; }
.edit-part-viewport .epx-icobtn:hover{ background: var(--ssw-tint-gray, #e2e4ee); color: var(--ssw-fg-1, #1f2733); }
.edit-part-viewport .epx-icobtn.del:hover{ background: var(--ssw-tint-red, #ffe4de); color:#f06548; }

/* density toggle */
.edit-part-viewport .epx-toggle{ position:relative; width:40px; height:21px; background: var(--ssw-tint-gray, #cfd4dd); border:none; border-radius:20px; cursor:pointer; transition:.15s; padding:0; }
.edit-part-viewport .epx-toggle.on{ background:#6d5fda; }
.edit-part-viewport .epx-toggle::after{ content:""; position:absolute; top:2px; left:2px; width:17px; height:17px; border-radius:50%; background: var(--ssw-surface, #fff); transition:.15s; }
.edit-part-viewport .epx-toggle.on::after{ left:21px; }

/* validation summary bar */
.edit-part-viewport .epx-vbar{ display:flex; align-items:center; gap:10px; background: var(--ssw-tint-yellow, #fff3da); border:1px solid #f3d489; border-radius:12px; padding:9px 14px; font-size:13px; color: var(--ssw-ink-orange, #8a5a00); }
.edit-part-viewport .epx-vlist{ background: var(--ssw-surface, #fff); border:1px solid #f3d489; border-radius:20px; padding:1px 10px; font-weight:600; font-size:12px; }

/* status timeline */
.edit-part-viewport .epx-timeline{ display:flex; align-items:center; background: var(--ssw-surface, #fff); border-radius:14px; box-shadow:0 6px 18px rgba(33,37,41,.08); padding:14px 18px; overflow-x:auto; }
.edit-part-viewport .epx-tl-step{ display:flex; align-items:center; gap:9px; white-space:nowrap; }
.edit-part-viewport .epx-tl-dot{ width:28px; height:28px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:14px; color:#fff; flex:none; }
.edit-part-viewport .epx-tl-done .epx-tl-dot{ background:#0ab39c; }
.edit-part-viewport .epx-tl-active .epx-tl-dot{ background:#6d5fda; box-shadow:0 0 0 4px #d7d2f7; }
.edit-part-viewport .epx-tl-todo .epx-tl-dot{ background: var(--ssw-tint-gray, #cfd4dd); }
.edit-part-viewport .epx-tl-label{ font-size:12.5px; font-weight:600; color: var(--ssw-fg-1, #1f2733); }
.edit-part-viewport .epx-tl-sub{ font-size:11px; color: var(--ssw-fg-2, #7b8190); }
.edit-part-viewport .epx-tl-line{ height:3px; width:42px; background: var(--ssw-surface-3, #e3e6ec); margin:0 12px; border-radius:2px; flex:none; }
.edit-part-viewport .epx-tl-line.done{ background:#0ab39c; }
`;

function SymbolButton({ label, onClick }) {
  return (
    <Button type="button" color="light" className="border fw-bold" onClick={onClick} style={{ minWidth: 44 }}>
      {label}
    </Button>
  );
}

function stringOrEmpty(value) {
  if (value == null) return "";
  return String(value).trim();
}

// Go-live parity (2026-09-24): legacy EditPart.vb treats a blank and a "-"
// Nominal/Min/Max the same way (both are saved as NULL).
function isBlankNumeric(value) {
  const v = String(value ?? "").trim();
  return v === "" || v === "-";
}

function nullableDecimalToString(value) {
  if (value == null) return "";
  return String(value);
}

function nullableApiDecimal(value) {
  const trimmed = String(value ?? "").trim();
  if (trimmed === "" || trimmed === "-") return 0;
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? 0 : parsed;
}


function getAdditionalInfoClass(row) {
  const dimensionType = String(row.DimensionType || "").toLowerCase().trim();
  const toleranceClass = String(row.ToleranceClass || "").toLowerCase().trim();

  if (toleranceClass.includes("reference")) return "edit-part-additional-reference";
  if (dimensionType.includes("note") || dimensionType.includes("*") || dimensionType.includes("finish")) return "edit-part-additional-note";
  if (dimensionType.includes("gd&t")) return "edit-part-additional-gdt";
  return "edit-part-additional-default";
}

function encodeReferenceSymbols(value) {
  return String(value || "")
    .replaceAll("/", "∕")
    .replaceAll("-", " - ")
    .replaceAll(":", " ▪ ")
    .replaceAll("<", "Ê ")
    .replaceAll(">", "Ë")
    .replaceAll("{", "Ì ")
    .replaceAll("}", "Í")
    .replaceAll("(", "È ")
    .replaceAll(")", "É")
    .replaceAll(",", "‚");
}

function decodeReferenceSymbols(value) {
  return String(value || "")
    .replaceAll("Ê", "<")
    .replaceAll("Ë", ">")
    .replaceAll("Ì", "{")
    .replaceAll("Í", "}")
    .replaceAll("È", "(")
    .replaceAll("É", ")");
}

function getSymbolMapping(kind, isNew, isReferenceStyle) {
  const maps = {
    slash: { isNew: "/", reference: "⁄", normal: "" },
    openParen: { any: "È" },
    closeParen: { any: "É" },
    openBrace: { any: "Ì" },
    closeBrace: { any: "Í" },
    lessThan: { any: "Ê" },
    greaterThan: { any: "Ë" },
    plusMinus: { any: "±" },
    degree: { any: "°" },
  };

  const m = maps[kind];
  if (!m) return { allowed: false, value: "" };
  if (m.any) {
    if (isNew) return { allowed: true, value: m.any };
    return isReferenceStyle ? { allowed: true, value: m.any } : { allowed: false, value: "" };
  }
  if (kind === "slash") {
    if (isNew) return { allowed: true, value: m.isNew };
    if (isReferenceStyle) return { allowed: true, value: m.reference };
    return { allowed: true, value: m.normal };
  }
  return { allowed: false, value: "" };
}

async function getCurrentUsername() {
  return getMafiaEmpId();
}

async function getLockStatus(request) {
  const response = await api.post("mafia/lockstatus", {
    partNumber: request.partNumber,
    rev: request.rev,
    revMinor: request.revMinor,
  });
  return response.data;
}

async function setLockStatus(request) {
  const response = await api.post("mafia/set-lock-status", {
    partNumber: request.partNum,
    rev: request.rev,
    revMinor: request.revMinor,
    newLock: request.newLock,
  });
  return response.data;
}

async function getOldHistory(request) {
  const response = await api.post("mafia/old-history", request);
  return response.data;
}

async function getRevisedBy(request) {
  const response = await api.post("mafia/revisedby", request);
  // The revisedby endpoint returns a bare value; now that identity is an EmpId
  // it can be numeric (e.g. 3325), which axios parses as a number and breaks
  // downstream .toLowerCase() calls. Always coerce to a string.
  return response.data == null ? "" : String(response.data);
}

async function getCreatedBy(request) {
  const response = await api.post("mafia/createdby", request);
  // Same reason as getRevisedBy: identity is now an EmpId and may be numeric.
  return response.data == null ? "" : String(response.data);
}

async function getPart(request) {
  const response = await api.post("mafia/part", request);
  return response.data;
}

async function getGageList() {
  const response = await api.get("mafia/gagelist");
  return response.data;
}

async function getChildren(partnum) {
  const response = await api.get("mafia/children", { params: { partnum } });
  return response.data;
}

async function determineMultiplier(dimType, toleranceClass, jobQuan) {
  const response = await api.post("mafia/determine-multiplier", null, {
    params: { dimType, toleranceClass, jobQuan }
  });
  return response.data;
}

async function getPartExistsMajor(partnum) {
  const response = await api.get("mafia/part-exists", { params: { partnum } });
  return response.data;
}

async function getPartExistsExact(request) {
  const response = await api.post("mafia/part-exists", request);
  return response.data;
}

// Go-live parity (2026-09-24): is this exact revision an in-progress draft?
async function getInProgress(request) {
  const response = await api.post("mafia/inprogress", request);
  return response.data === true;
}

async function addLine(payload) {
  const response = await api.post("mafia/add-line", payload);
  return response.data;
}

async function addLineUnfinished(payload) {
  const response = await api.post("mafia/add-line-unfinished", payload);
  return response.data;
}

async function deleteMafiaTable(partnum, revMajor, revMinor) {
  const response = await api.delete("mafia/deletemafiatable", {
    params: { partnum, revMajor, revMinor }
  });
  return response.data;
}

async function updateRevMinor(request) {
  const response = await api.post("mafia/update-revminor", request);
  return response.data;
}

async function addChildPartApi(request) {
  const response = await api.post("mafia/add-child-part", request);
  return response.data;
}

async function deleteChild(partnum) {
  const response = await api.delete("mafia/delete-children", { params: { partnum } });
  return response.data;
}

async function sendEmailPartEdited(request) {
  const response = await api.post("mafia/email-part-edited", request);
  return response.data;
}

// Go-live parity (2026-09-24, audit S7): highest print revision of a part
// (any status) — GET mafia/part-exists returns MAX(Rev).
async function getMaxPrintRev(partnum) {
  const response = await api.get("mafia/part-exists", { params: { partnum } });
  return response.data;
}

async function getLatestMajorRev(partnum) {
  const response = await api.get("mafia/latest-major-rev", { params: { partnum } });
  return response.data;
}

async function getLatestRevMinor(request) {
  const response = await api.post("mafia/latest-revminor", request);
  return response.data;
}

// ── Round 63: stage-aware load errors for Edit Part ──────────────────────────
// The load chain is seven backend calls; a failure anywhere used to surface
// only "Failed to load Edit Part page." — undiagnosable, reported in UAT as
// intermittent "load data failed". Each stage is now labelled and carries the
// backend's own error text, and the page offers Retry instead of dead-ending.
function extractEditApiError(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data && typeof data.message === "string" && data.message.trim()) {
    return data.message.trim();
  }
  if (error?.response?.status === 403) {
    return "You do not have permission to perform this action.";
  }
  if (error?.response?.status) {
    return `Request failed with status ${error.response.status}.`;
  }
  if (error?.message) return error.message;
  return fallback;
}

async function editStage(stage, fn) {
  try {
    return await fn();
  } catch (error) {
    const detail = extractEditApiError(error, "request failed");
    const wrapped = new Error(`${stage} — ${detail}`);
    wrapped.isEditStageError = true;
    throw wrapped;
  }
}
